// getCookie
import { getCookie } from '../utils/cookies.js';
import { pauseAllMedia, removeTrapFocus } from '../global/global.js';

class ModalDialog extends HTMLElement {
  constructor() {
    super();

    if (this.querySelectorAll('[id^="ModalClose-"]').length > 0) {
      this.querySelectorAll('[id^="ModalClose-"]').forEach((closeBtn) => {
        closeBtn.addEventListener('click', this.hide.bind(this, false));
      });
    }

    if (this.querySelector('[id^="ModalCloseNoThanks-"]')) {
      this.querySelector('[id^="ModalCloseNoThanks-"]').addEventListener(
        'click',
        this.hide.bind(this, false),
      );
    }

    this.addEventListener('keyup', this.onKeyUp.bind(this));


    if (this.classList.contains('media-modal')) {
      this.addEventListener('pointerup', (event) => {
        if (
          event.pointerType === 'mouse' &&
          !event.target.closest('deferred-media, product-model')
        )
          this.hide();
      });

    } else {
      this.addEventListener('click', (event) => {
        if (event.target === this) this.hide();
      });

    }
  }

  onKeyUp(event) {
    if (event.code.toUpperCase() === 'ESCAPE') this.hide();
  }

  show(opener) {
    this.openedBy = opener;
    const popup = this.querySelector('.template-popup');
    document.body.classList.add('overflow-hidden');
    this.setAttribute('open', '');
    if (popup) popup.loadContent();
    trapFocus(this, this.querySelector('[role="dialog"]'));
    pauseAllMedia();
  }

  hide() {
    this.openedBy = opener;
    document.body.classList.remove('overflow-hidden');
    document.body.dispatchEvent(new CustomEvent('modalClosed'));
    this.removeAttribute('open');
    removeTrapFocus(this.openedBy);
    pauseAllMedia();

    if (typeof this.removeForm === 'function') {
      this.removeForm();
    }
  }
}
customElements.define('modal-dialog', ModalDialog);

/* ============================================================================
  Social Feed Modal - Extends default Popup
============================================================================== */
class SocialFeedPopup extends ModalDialog {
  constructor() {
    super();
    this.activePost;

    this.addEventListener('click', (e) => {
      if (e.target.closest('.js-sm-feed-popup-left-arrow'))
        this.switchSlides('left');
      if (e.target.closest('.js-sm-feed-popup-right-arrow'))
        this.switchSlides('right');
    });
  }

  setActivePost(postId) {
    const relevantPost = this.querySelector(`[data-post-id="${postId}"]`);
    if (!relevantPost) return;
    if (this.activePost) this.activePost.classList.remove('is-active');
    relevantPost.classList.add('is-active');
    this.activePost = relevantPost;
    pauseAllMedia();
    this.playVideo();
  }

  playVideo() {
    const video = this.activePost.querySelector('video');
    if (!video) return;
    setTimeout(() => {
      video.play();
    }, 400);
  }

  pauseVideo() {
    const video = this.activePost.querySelector('video');
    if (!video) return;
    video.pause();
  }

  switchSlides(direction = 'right') {
    const newPost =
      direction === 'left'
        ? this.activePost.previousElementSibling
        : this.activePost.nextElementSibling;

    this.pauseVideo();

    this.activePost.classList.remove('is-active');
    newPost.classList.add('is-active');
    this.activePost = newPost;

    this.playVideo();
  }
}

customElements.define('social-feed-popup', SocialFeedPopup);

/* ============================================================================
  Gallery Viewer Modal - Extends default Popup
============================================================================== */
class GalleryViewer extends ModalDialog {
  constructor() {
    super();
    this.carousel = this.querySelector('embla-component');
  }

  setActiveImage(position) {
    setTimeout(() => {
      this.carousel.slideToIndex(position);
    }, 100);
  }
}

customElements.define('gallery-viewer', GalleryViewer);

/* ============================================================================
  Quick view modal - Extends default Popup
============================================================================== */
class ProductPopup extends ModalDialog {
  constructor() {
    super();
  }

  static get observedAttributes() {
    return ['data-id'];
  }

  getForm(_handle, _id) {
    const shopifyProductUrl = `/products/${_handle}?view=quick-view`;
    fetch(shopifyProductUrl)
      .then((res) => {
        return res.text();
      })
      .then((res) => {
        let parser = new DOMParser();
        let doc = parser.parseFromString(res, 'text/html');
        let productForm = doc.querySelector('.product-popup-inner');
        const productPopup = document.querySelector('product-popup');
        productPopup.setAttribute('data-id', _id);
        const quickViewCard = document.querySelector(
          'product-popup .quick-view-card',
        );
        if (quickViewCard) quickViewCard.appendChild(productForm);
        quickViewCard.focus();

        quickViewCard
          .querySelector('[id^="popup-closer-"]')
          .addEventListener('click', () => {
            productPopup.hide();
          });
      })
      .catch((err) => {});
  }

  removeForm() {
    const quickViewCard = document.querySelector(
      'product-popup .quick-view-card',
    );
    if (quickViewCard) quickViewCard.lastElementChild.remove();
  }

  attributeChangedCallback(name, oldValue, newValue) {
    let _id = this.getAttribute('data-id');
  }
}

customElements.define('product-popup', ProductPopup);

/* ============================================================================
  Modal Opener
============================================================================== */

class ModalOpener extends HTMLElement {
  constructor() {
    super();

    const button = this.querySelector('[id^="ModalOpenerBtn-"]');

    if (!button) return;

    // if keybaord has focused on the modal trigger
    button.addEventListener('keydown', (e) => {
      if(e.code == "Enter") {
        document.activeElement.click();
      }
    })

    button.addEventListener('click', (e) => {
      e.preventDefault();
      const modal = document.querySelector(this.getAttribute('data-modal'));
      if (!modal) return;

      // checking for the social feed popup that needs to show correct content
      if (modal.classList.contains('social-feed-popup')) {
        const postId = button.getAttribute('data-post-id');
        if (button.getAttribute('data-post-id') === 'post-placeholder') return;
        modal.setActivePost(postId);
      }

      // checking for the PDP gallery viewer that needs to show correct content
      if (modal.classList.contains('gallery-viewer')) {
        const imagePosition = +button.getAttribute('data-index');
        modal.setActiveImage(imagePosition);
      }

      // checking for the product quick view that needs to render correct form
      if (modal.classList.contains('product-popup')) {
        e.preventDefault();
        modal.getForm(
          this.getAttribute('data-handle'),
          this.getAttribute('data-id'),
        );
      }

      modal.show(button);
    });
  }
}
customElements.define('modal-opener', ModalOpener);

/* ============================================================================
  Deferred Media
============================================================================== */
class DeferredMedia extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    const poster = this.querySelector('[id^="Deferred-Poster-"]');
    if (!poster) return;
    poster.addEventListener('click', this.loadContent.bind(this));
  }

  loadContent(focus = true) {
    pauseAllMedia();
    if (!this.getAttribute('loaded')) {
      const content = document.createElement('div');
      content.appendChild(this.querySelector('template').content.firstElementChild.cloneNode(true));

      this.setAttribute('loaded', true);
      this.classList.add('is-active');
      const deferredElement = this.appendChild(
        content.querySelector('video, model-viewer, iframe'),
      );
      if (focus) deferredElement.focus();
      if (
        deferredElement.nodeName == 'VIDEO' &&
        deferredElement.getAttribute('autoplay')
      ) {
        // force autoplay for safari
        deferredElement.play();
      }

      this.addEventListeners();
    } else {
      this.toggleVideoStateHandler('PLAY');
      this.playVideo();
    }
  }

  addEventListeners() {
    const video = this.querySelector('video');
    video?.addEventListener('pause', (e) => {
      this.toggleVideoStateHandler('PAUSE', video);
    });

    video?.addEventListener('ended', (e) => {
      this.toggleVideoStateHandler('END', video);
    });
  }

  toggleVideoStateHandler(action, video) {
    if (action === 'PLAY') {
      this.classList.add('is-active');
    } else if (action === 'PAUSE' || action === 'END') {
      this.classList.remove('is-active');
    }
  }

  playVideo() {
    const type = this.getAttribute('data-type');
    if (type === 'youtube') {
      this.querySelector('.js-youtube')?.contentWindow.postMessage('{"event":"command","func":"playVideo","args":""}', '*',);
    } else if (type == 'video') {
      this.querySelector('video')?.play();
    } else if (type == 'vimeo') {
      this.querySelector('.js-vimeo')?.contentWindow.postMessage('{"method":"play"}', '*');
    } else {
      console.error('No video type specified');
    }
  }
}

customElements.define('deferred-media', DeferredMedia);

/* ============================================================================
  Signup Modal
============================================================================== */

if (!customElements.get('signup-modal')) {
  customElements.define(
    'signup-modal',
    class SignupModal extends ModalDialog {
      constructor() {
        super();

        this.modalClose = this.querySelector('[js-modal="close"]');

        // Grab cookie values on mount

        // Shopify generated
        // This custom el
        const hasModalClosed = this.getCookie('signup-modal-closed');

        if (window.designMode.enabled !== 'true') {
          this.delay = parseInt(this.dataset.delay) || 9000;
          this.checkSubmission(
            hasModalClosed
          );
          this.addEventListeners();
        } else {
          this.addEventListeners();
        }

        this.forceOpen();
      }

      /**
       * Check if the form has been submitted
       * This is checking for the URL param ?customer_posted=true
       */
      checkSubmission(l, c) {
        // Get URL parameters
        const urlParams = new URLSearchParams(window.location.search);
        const customerPosted = urlParams.get('customer_posted');

        if (!customerPosted) {
          this.checkCookie(l, c);
          return;
        }

        if (customerPosted === 'true') {
          this.show();
          setCookie('signup-modal-closed', 'true', 340);
          this.successMessage();
        } else {
          this.checkCookie(l, c);
        }
      }

      /**
       * Check if the cookie has been set
       * If it hasn't been set, show the modal
       *
       * @param {String} l - Locale (Shopiy) - Shopify default predicted locale code
       * @param {String} c - Closed - Has the signup modal closed?
       */
      checkCookie() {
        if (this.getCookie('signup-modal-closed') == undefined) {
          setTimeout(() => {
            this.show();
          }, this.delay);
        }
      }

      successMessage() {
        const items = document.querySelectorAll('.js-sign-up-success');

        items.forEach((item) => {
          item.classList.add('is-hidden');
        });
      }

      hide() {
        super.hide();
        this.setCookie('signup-modal-closed', 'true', 30);
        this.videoStop();
      }

      show(opener) {
        super.show(opener);
        this.videoPlay();
      }

      videoPlay() {
        const video = this.querySelector('video');
        if (video) {
          video.play();
        }
      }

      videoStop() {
        const video = this.querySelector('video');
        if (video) {
          video.pause();
        }
      }

      /**
       * This is for the theme editor
       * This will display the modal when the user is editing the section
       */
      addEventListeners() {
        document.addEventListener('shopify:section:select', (event) => {
          this.hide();
          const id = event.detail.sectionId;
          if (id != 'sign-up-modal') {
            return;
          }
          const opener = `#SignupModal-${id} button`;
          this.show(opener);
        });

        document.addEventListener('shopify:section:deselect', (event) => {
          this.hide();
        });

        this.modalClose.addEventListener('click', () => {
          this.hide();
        });

        // on key escape close modal
        document.addEventListener('keydown', (e) => {
          if (e.key === 'Escape') {
            this.hide();
          }
        });
      }

      forceOpen() {
        const urlParams = new URLSearchParams(window.location.search);
        const signup_modal = urlParams.get('signup_modal');
        const form_type = urlParams.get('form_type');

        // if url contains /challenge
        if (window.location.pathname.includes('/challenge')) {
          return;
        }

        if (signup_modal === 'true' || form_type === 'customer') {
          this.show();
        }
      }

      getCookie(name) {
        const value = '; ' + document.cookie;
        const parts = value.split('; ' + name + '=');
        if (parts.length === 2) return parts.pop().split(';').shift();
      }

      setCookie(name, value, days) {
        let expires = '';
        if (days) {
          const date = new Date();
          date.setTime(date.getTime() + days * 24 * 60 * 60 * 1000);
          expires = '; expires=' + date.toUTCString();
        }
        document.cookie = name + '=' + (value || '') + expires + '; path=/';
      }
    },
  );
}
